/**
 * The import pipeline's tenant API — typed wrappers over `/v1/twin/import/{uploads,jobs,extractors,agent-keys}`
 * plus their React Query hooks (Phase 2 slices 1-3; backend `internal/twin/import_{intake,registry,agent_keys}.go`,
 * contract `api/openapi.yaml` ImportJob, ImportExtractor…). The hooks live here, not in queries.ts, so the
 * two sessions never edit that file's body at once.
 *
 * What the server decides, and the UI only shows:
 *   - an upload is stored once per org; the same file while its job runs answers with that job, and after it
 *     imported, with a `duplicate` job naming the one that did;
 *   - only a PLATFORM admin registers, approves or rejects an extractor version (the runner executes its code
 *     in the cloud), and a brain draft only after its dry run passed;
 *   - an approved version writes only its manifest's params, onto the equipment bound at approval;
 *   - a known document family's file (a laboratory certificate) goes to the family's product instead of import, when
 *     the org holds that product (the documents lane, migration 0228): the upload answers the document, no job. From
 *     an org that does not hold it, the file's job is held with a reason until the product is granted.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

export type ImportJobStatus =
  | "queued"
  | "ready"
  | "applying"
  | "done"
  | "duplicate"
  | "awaiting_author"
  | "validating"
  | "awaiting_approval"
  | "failed"
  | "cancelled"
  // a database file (dBASE, Firebird) no reader exists for yet: kept, waiting; reprocess releases it. With a
  // hold_reason (migration 0228): a document family's file waiting for its product to be granted to the org
  | "held";

/** One note an uploader left with a file (migration 0266): plain text, shown as text. Never who wrote it. */
export interface ImportFileNote {
  text: string;
  created_at: string;
}

/** The longest note an upload carries, in characters (the server's cap; it refuses a longer one, 400 invalid_note). */
export const IMPORT_NOTE_MAX = 1000;

/** A note's length as the server counts it: characters (code points), not UTF-16 units, so a letter outside the basic
 * plane counts once. */
export function noteChars(note: string): number {
  return Array.from(note).length;
}

export interface ImportFile {
  id: string;
  filename: string;
  content_type: string;
  byte_size: number;
  sha256: string;
  purged: boolean;
  /** What the people who uploaded the file wrote about it (migration 0266): its 10 newest notes, oldest first. Only in
   *  the jobs list and one job's answer; absent when it has none. Kept on the file, whatever job it came with. */
  notes?: ImportFileNote[];
}

export interface ImportJob {
  id: string;
  status: ImportJobStatus;
  origin: "web" | "agent" | "copilot" | "backfill";
  /** The work (migration 0239, #1428): import, a file to import; revision, the author revises the reader in use on this
   *  file; reapply, a file the revised reader imported, read again with its next version. Absent from an older server. */
  kind?: "import" | "revision" | "reapply";
  duplicate_of?: string;
  /** Why the job failed; `routed_to_documents` for a job cancelled because its file went to documents. */
  error?: string;
  /** Only on a held job (migration 0228): why it waits when it is not for a database reader, "document family
   *  <family>: product <product> not enabled". The product's grant sends the file to it. */
  hold_reason?: string;
  /** Only on a cancelled job (migration 0228): the document its file became. Reprocess refuses such a job. */
  routed_document_id?: string;
  created_at: string;
  updated_at: string;
  finished_at?: string;
  file: ImportFile;
  /** Only on a job a plant agent sent: how its file reached the cloud, direct or through a gateway. */
  delivered_via?: "direct" | "gateway";
  /** Through a gateway of the org: its thing id and name, e.g. "Gateway 7E1A90A1". */
  gateway_id?: string;
  gateway_name?: string;
  /** Why the job's file failed, as far as the file shows it (migration 0259); absent when it did not fail on its file. */
  diagnosis?: ImportDiagnosis;
  /** On a failed import (migration 0260): the revision of its reader it opened or joined, and where that stands. The
   *  file imports with the revision's approval. */
  revision_job_id?: string;
  revision_status?: ImportJobStatus;
  /** On a failed or cancelled job: the job of the same file that stands against reprocessing it, done or in flight
   *  (the one reprocess refuses it for, 409 file_has_another_job), and when it finished, else began. */
  replaced_by?: { id: string; status: ImportJobStatus; at: string };
}

/** A date order: day, month or year first; ambiguous when no day is over 12; conflict when the values disagree. */
export type ImportDiagnosisOrder = "dmy" | "mdy" | "ymd" | "ambiguous" | "conflict";

/** A column the header gives no name (column_N) that holds data. */
export interface ImportDiagnosisUnnamed {
  name: string;
  position: number;
  /** The header has a blank cell for it (it ends with its delimiter). */
  named_empty: boolean;
  /** Only unnamed columns follow it. */
  at_end: boolean;
  filled_rows: number;
  example: string;
}

/**
 * The runner's diagnosis of a failed read (forge_extract diagnose.py; R1 of
 * docs/plans/2026-10-19_reader-layout-change.md): the cell and value the reader raised on, what it expected there, and
 * the file's layout facts. reader_line reaches a platform admin only.
 */
export interface ImportDiagnosis {
  error?: string;
  value?: string;
  column?: string;
  /** The data row: 1 is the first filled row under the header (a blank row is not counted). */
  row?: number;
  table?: string;
  expected_order?: ImportDiagnosisOrder;
  found_order?: ImportDiagnosisOrder;
  note?: string;
  reader_line?: { line: number; code?: string };
  facts?: {
    table?: string;
    header_names?: number;
    rows?: number;
    unnamed_columns?: ImportDiagnosisUnnamed[];
    date_orders?: Record<string, ImportDiagnosisOrder>;
    missing_columns?: string[];
    added_columns?: string[];
  };
}

/**
 * A file the documents lane sent to another product instead of importing it (migration 0228; plan of record
 * docs/plans/2026-10-13_microvision-and-the-account-layer.md, section D), never its bytes: the server's
 * documents.Document. `state` is what the product's consumer sees.
 */
export interface RoutedDocument {
  id: string;
  /** The document family, e.g. a laboratory certificate's. */
  family: string;
  /** The product that reads it (a suite product's key, e.g. "microvision"). */
  product: string;
  /** The product's display name: where an upload's message says the file was sent. */
  product_name: string;
  file_name: string;
  byte_size: number;
  sha256: string;
  state: "new" | "taken" | "done";
  /** While taken: when the lease runs out and the document is new again. */
  lease_until?: string;
  /** Taken or done: who took it last. */
  taken_by?: string;
  /** Done: the consumer's own reference to what it made of the file. */
  consumer_ref?: string;
  received_at: string;
  purged: boolean;
}

/** An upload's answer when its file went to the documents lane: no job was opened. */
export interface ImportUploadRouted {
  document: RoutedDocument;
}

/** What an upload answers: the file's job, or the document it became (201 when this upload made it, 200 when the same
 *  file was one already). */
export type ImportUploadAnswer = ImportJob | ImportUploadRouted;

/** Whether an upload's answer is the document its file became: by the document it carries, never by a missing id. */
export function isRoutedUpload(answer: ImportUploadAnswer): answer is ImportUploadRouted {
  const doc = (answer as Partial<ImportUploadRouted>).document;
  return typeof doc === "object" && doc !== null;
}

export interface ImportJobList {
  items: ImportJob[];
  limit: number;
  offset: number;
}

export interface ManifestParamDoc {
  source?: string;
  tag: string;
  unit?: string;
  data_kind?: string;
  equipment?: string;
}

export interface ManifestStreamDoc {
  key: string;
  mode: string;
  kind?: string;
  equipment?: string;
  params?: ManifestParamDoc[];
  /** The source columns the time is read from, and the records' other fields (heat, part, counts): for coverage. */
  time_cols?: string[];
  identity_cols?: string[];
  /** Where the stream's file comes from, as its reader says (#1649): "history" or "report"; absent for none. */
  source_role?: string;
}

/**
 * One of the author's questions in four parts (F3): what it saw in the file, what it assumed, and what changes if
 * that is wrong, with how much it holds up: "blocks_import", "blocks_analysis" or "good_to_know".
 */
export interface ImportAuthorQuestion {
  saw?: string;
  assume?: string;
  changes?: string;
  priority?: string;
}

/** Where one source column goes (F3): "<stream>.<tag>", "lot.<field>", "product.<field>" or "skip". */
export interface ImportPlacement {
  sheet?: string;
  column?: string;
  to?: string;
  reason?: string;
}

/** A defect word of the plant's and the canonical defect the author proposes for it; null when it found none. */
export interface ImportVocabularyProposal {
  word?: string;
  taxonomy_id?: string | null;
  note?: string;
}

/** The author's notes for its reviewer. Every field is optional, and a list that would be empty is absent: older
 *  drafts have assumptions and questions alone. */
export interface ImportAuthorNotes {
  assumptions?: string[];
  /** Every question as a line of text (F3 keeps them text, so a page that knows no other form reads them), a
   *  four-part one in extract_author.py question_line's form; an object here is still read, defensively. */
  questions?: (string | ImportAuthorQuestion)[];
  /** The four-part questions as themselves (F3): shown, with only the text lines that are not one of them. */
  structured_questions?: ImportAuthorQuestion[];
  placements?: ImportPlacement[];
  vocabulary?: ImportVocabularyProposal[];
  /** Each equipment key of the manifest and the plant's item the author proposes for it, or null when none fits
   *  (plan v21 B3, #1400): the author never invents one. Read by lib/imports/proposal. */
  equipment?: ImportAuthorEquipment[];
  /** After a send-back (plan v21 B5): what the author did with each instruction of the note and each corrected answer. */
  review_response?: ImportReviewResponseItem[];
}

export interface ImportReviewResponseItem {
  item: string;
  done: boolean;
  /** How it was done, or why it was not. */
  how: string;
}

/** The reviewer's answer to one of a draft's four-part questions (plan v21 B5): index is its place in the draft's
 *  author_notes.structured_questions; keep, its assumption stands (no text); else text is the correction. */
export interface ImportReviewAnswer {
  index: number;
  keep: boolean;
  text?: string;
}

export interface ImportAuthorEquipment {
  role: string;
  entity_id: string | null;
  why: string;
}

export interface ImportStream {
  id: string;
  key: string;
  kind: "append" | "snapshot";
  data_source_id: string | null;
  bindings: Record<string, string>;
}

export interface ImportExtractorVersion {
  id: string;
  extractor_id: string;
  version: number;
  status: "draft" | "active" | "retired" | "rejected";
  source_sha256: string;
  helper_version: string;
  author_job_id?: string;
  review_note?: string;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  signature?: Record<string, unknown>;
  manifest?: {
    key?: string;
    label?: string;
    streams?: ManifestStreamDoc[];
    /** Source columns the extractor does not import, each with why. */
    ignore?: Record<string, string>;
  } & Record<string, unknown>;
  /** The brain's self-test of its draft: ok, errors, violations, warnings, held, preview, report, rounds. */
  selftest?: Record<string, unknown>;
  /** The cloud's dry run on the file: ok, errors, preview (rows, days and a sample per stream), report (coverage,
   *  stats, skipped, a sample of what was set aside). */
  validation?: { ok?: boolean; errors?: string[]; preview?: unknown; report?: unknown } & Record<string, unknown>;
  author_notes?: ImportAuthorNotes & Record<string, unknown>;
  /** The answers the reviewer gave when THIS version was sent back (single version reads only). */
  review_answers?: ImportReviewAnswer[];
  source?: string;
  streams?: ImportStream[];
  /** Only on the approval of a revision (#1428): the retired version's other files queued to be read again, and those
   *  it could not queue. */
  reapply?: { queued: number; skipped?: string[] };
}

/** The answer to asking for a revision of the reader in use (#1428): the version, with the request on it, and the
 *  revision job that waits for the author. */
export interface ImportRevisionAnswer {
  version: ImportExtractorVersion;
  job: ImportJob;
}

export interface ImportExtractor {
  id: string;
  key: string;
  label: string;
  created_at: string;
  versions?: ImportExtractorVersion[];
}

export interface ImportApproveRequest {
  streams: Record<string, ImportApproveStream>;
}

/**
 * One stream of an approval: its equipment bindings, optionally the data source it writes through, and optionally what
 * that data source's file is (one series per port, S1b): "history", "report" or null for none. Left out, the stream's
 * manifest role is written only where the data source has none; sent, it is written whatever the source holds.
 */
export interface ImportApproveStream {
  bindings: Record<string, string>;
  data_source_id?: string;
  source_role?: "history" | "report" | null;
}

export interface ImportAgentKey {
  id: string;
  label: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  expires_at: string | null;
}

export interface ImportAgentKeyCreated {
  id: string;
  label: string;
  prefix: string;
  created_at: string;
  expires_at: string | null;
  /** The key itself, shown this once. */
  key: string;
}

const enc = encodeURIComponent;

export const twinImports = {
  /** One file to the intake, with the uploader's note about it when there is one (the server trims it and keeps it on
   *  the stored file; an empty note is no note), and for a PDF the grade it is a process sheet for (P6). */
  upload: (file: File, note?: string, grade?: string) => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    if (note && note.trim() !== "") fd.append("note", note);
    if (grade && grade.trim() !== "") fd.append("grade", grade.trim());
    return api.postForm<ImportUploadAnswer>("/v1/twin/import/uploads", fd);
  },
  jobs: {
    list: (params: { limit?: number; status?: ImportJobStatus } = {}) => {
      const q = new URLSearchParams();
      q.set("limit", String(params.limit ?? 50));
      if (params.status) q.set("status", params.status);
      return api.get<ImportJobList>(`/v1/twin/import/jobs?${q.toString()}`);
    },
    cancel: (id: string) => api.post<ImportJob>(`/v1/twin/import/jobs/${enc(id)}/cancel`, {}),
    reprocess: (id: string) => api.post<ImportJob>(`/v1/twin/import/jobs/${enc(id)}/reprocess`, {}),
  },
  extractors: {
    list: () => api.get<{ items: ImportExtractor[] }>("/v1/twin/import/extractors"),
    version: (extractorId: string, version: number) =>
      api.get<ImportExtractorVersion>(`/v1/twin/import/extractors/${enc(extractorId)}/versions/${version}`),
    approve: (extractorId: string, version: number, body: ImportApproveRequest) =>
      api.post<ImportExtractorVersion>(
        `/v1/twin/import/extractors/${enc(extractorId)}/versions/${version}/approve`,
        body,
      ),
    reject: (extractorId: string, version: number, note: string, answers: ImportReviewAnswer[] = []) =>
      api.post<ImportExtractorVersion>(`/v1/twin/import/extractors/${enc(extractorId)}/versions/${version}/reject`, {
        note,
        ...(answers.length > 0 ? { answers } : {}),
      }),
    /** Ask the author to revise the version in use (#1428), with a note and answers to its questions, as a send-back. */
    revise: (extractorId: string, version: number, note: string, answers: ImportReviewAnswer[] = []) =>
      api.post<ImportRevisionAnswer>(`/v1/twin/import/extractors/${enc(extractorId)}/versions/${version}/revise`, {
        note,
        ...(answers.length > 0 ? { answers } : {}),
      }),
  },
  agentKeys: {
    list: () => api.get<{ items: ImportAgentKey[] }>("/v1/twin/import/agent-keys"),
    create: (body: { label: string; expires_at?: string }) =>
      api.post<ImportAgentKeyCreated>("/v1/twin/import/agent-keys", body),
    revoke: (id: string) => api.delete<void>(`/v1/twin/import/agent-keys/${enc(id)}`),
  },
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** Live fetches require the build flag AND an authenticated, org-resolved session. */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const IMPORT_JOBS_KEY = ["twin-import-jobs"] as const;
export const IMPORT_EXTRACTORS_KEY = ["twin-import-extractors"] as const;
export const IMPORT_AGENT_KEYS_KEY = ["twin-import-agent-keys"] as const;
export const importVersionKey = (extractorId: string, version: number) =>
  ["twin-import-extractor-version", extractorId, version] as const;

const IN_FLIGHT = new Set<ImportJobStatus>([
  "queued",
  "ready",
  "applying",
  "awaiting_author",
  "validating",
  "awaiting_approval",
]);

/**
 * The org's import jobs, newest first. While any job is still moving the list polls every 10 s, so a file
 * dropped here is seen through probe and import without a reload; once all are finished it stops.
 */
export function useImportJobsQuery() {
  return useQuery({
    queryKey: IMPORT_JOBS_KEY,
    queryFn: () => twinImports.jobs.list({ limit: 50 }),
    enabled: useLiveEnabled(),
    staleTime: 5_000,
    refetchInterval: (q) => ((q.state.data?.items ?? []).some((j) => IN_FLIGHT.has(j.status)) ? 10_000 : false),
  });
}

export function useUploadImportFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, note, grade }: { file: File; note?: string; grade?: string }) =>
      twinImports.upload(file, note, grade),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IMPORT_JOBS_KEY });
    },
  });
}

export function useCancelImportJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinImports.jobs.cancel(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IMPORT_JOBS_KEY });
    },
  });
}

export function useReprocessImportJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinImports.jobs.reprocess(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IMPORT_JOBS_KEY });
    },
  });
}

export function useImportExtractorsQuery() {
  return useQuery({
    queryKey: IMPORT_EXTRACTORS_KEY,
    queryFn: () => twinImports.extractors.list(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/** One version with its source, manifest, dry run and the author's notes: what a reviewer reads. */
export function useImportExtractorVersionQuery(extractorId: string | null, version: number | null) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: importVersionKey(extractorId ?? "", version ?? 0),
    queryFn: () => twinImports.extractors.version(extractorId as string, version as number),
    enabled: live && !!extractorId && !!version,
    staleTime: 30_000,
  });
}

function useVersionDecision<T>(fn: (v: T) => Promise<ImportExtractorVersion>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      // Approval readies (and retirement requeues) jobs; rejection sends one back to its author.
      void qc.invalidateQueries({ queryKey: IMPORT_EXTRACTORS_KEY });
      void qc.invalidateQueries({ queryKey: IMPORT_JOBS_KEY });
    },
  });
}

export function useApproveImportVersion() {
  return useVersionDecision((v: { extractorId: string; version: number; body: ImportApproveRequest }) =>
    twinImports.extractors.approve(v.extractorId, v.version, v.body),
  );
}

export function useRejectImportVersion() {
  return useVersionDecision((v: { extractorId: string; version: number; note: string; answers?: ImportReviewAnswer[] }) =>
    twinImports.extractors.reject(v.extractorId, v.version, v.note, v.answers),
  );
}

/** Ask for a revision of the version in use (#1428): a revision job opens for the author, and the reader's card shows
 *  the request. */
export function useReviseImportVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { extractorId: string; version: number; note: string; answers?: ImportReviewAnswer[] }) =>
      twinImports.extractors.revise(v.extractorId, v.version, v.note, v.answers),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IMPORT_EXTRACTORS_KEY });
      void qc.invalidateQueries({ queryKey: IMPORT_JOBS_KEY });
    },
  });
}

/** The org's plant-agent keys (org admins only; the server answers 403 to anyone else). */
export function useImportAgentKeysQuery(enabled: boolean) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: IMPORT_AGENT_KEYS_KEY,
    queryFn: () => twinImports.agentKeys.list(),
    enabled: live && enabled,
    staleTime: 30_000,
  });
}

export function useCreateImportAgentKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label: string; expires_at?: string }) => twinImports.agentKeys.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IMPORT_AGENT_KEYS_KEY });
    },
  });
}

export function useRevokeImportAgentKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twinImports.agentKeys.revoke(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IMPORT_AGENT_KEYS_KEY });
    },
  });
}
